import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import Logo from "@/components/Logo";
import ForgotPassword from "@/components/ForgotPassword";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome, ${user.name}`);
      // Drivers land straight on their route screen — everyone else on the dashboard.
      // Route per role: driver → route screen, admin → Command Centre, others → Dashboard
      const dest = user.role === "driver" ? "/driver"
        : user.role === "admin" ? "/app/ops"
        : "/app";
      nav(dest);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" style={{ background: "var(--bone)" }}>
      {/* Left – form */}
      <div className="flex flex-col justify-center px-8 md:px-16 lg:px-24 py-14 relative">
        <Link to="/" className="self-start mb-14 hover:opacity-80 transition-opacity" data-testid="login-back-home">
          <Logo />
        </Link>

        <div className="max-w-md w-full">
          <div className="hbt-label mb-4">Sign in</div>
          <h1 className="font-display font-semibold text-4xl md:text-5xl tracking-tight leading-tight">Welcome back.</h1>
          <p className="text-neutral-500 mt-3 text-sm">Access the client portal or your HBT operations workspace.</p>

          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="login-form">
            <div>
              <label className="hbt-label block mb-2">Email</label>
              <input type="email" required className="hbt-input" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="hbt-label">Password</label>
                <button type="button" onClick={() => setShowForgot(true)} className="text-xs text-neutral-500 hover:text-[var(--forest)] hover:underline" data-testid="forgot-password-link">
                  Forgot password?
                </button>
              </div>
              <input type="password" required className="hbt-input" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" />
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="login-submit-btn">
              {loading ? "Signing in…" : (<>Sign in <ArrowRight className="w-4 h-4" /></>)}
            </button>
          </form>
        </div>
      </div>

      {/* Right – visual */}
      <div className="hidden lg:block relative overflow-hidden" style={{ background: "var(--forest)" }}>
        <img
          src="/logo/login-bg.webp"
          className="absolute inset-0 w-full h-full object-cover opacity-50"
          alt=""
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(26,54,45,0.55), rgba(26,54,45,0.85))" }} />
        <div className="relative z-10 h-full flex flex-col justify-between p-14 text-white">
          <div className="hbt-label" style={{ color: "rgba(255,255,255,0.7)" }}>HBT Waste Management</div>
          <div>
            <div className="font-display font-semibold text-4xl xl:text-5xl tracking-tight leading-tight max-w-lg">
              We measure what we manage — and prove what we divert.
            </div>
            <div className="mt-8 flex items-center gap-6">
              <div>
                <div className="font-display text-3xl font-semibold">63%</div>
                <div className="text-xs uppercase tracking-[0.2em] opacity-70 mt-1">Landfill diversion</div>
              </div>
              <div className="w-px h-10 bg-white/20" />
              <div>
                <div className="font-display text-3xl font-semibold">4</div>
                <div className="text-xs uppercase tracking-[0.2em] opacity-70 mt-1">Waste streams</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ForgotPassword open={showForgot} onClose={() => setShowForgot(false)} />
    </div>
  );
}
